import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

const authHeader = (authorization: string) => ({
    "Content-Type": "application/json",
    Authorization: authorization,
})

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

export interface MerchantIfood {
    id: string
    name: string
    associada: boolean
}

export interface MerchantStatusIfood {
    merchantName: string | null
    operation: string | null
    available: boolean
    state: string | null
    title: string | null
    subtitle: string | null
}

export interface StatusIfood {
    interno: boolean
    credenciaisDefinidas: boolean
    merchantUUIDAssociado: string | null
    merchantAssociado: boolean
    merchants: MerchantIfood[]
    merchantsStatus: MerchantStatusIfood[]
    codigoAutorizacao: string | null
    cardapiosImportados: string[]
    hasCardapioImportado: boolean
}

export interface UserCodeIfood {
    userCode: string | null
    authorizationCodeVerifier: string
    verificationUrlComplete: string
}

export interface MensagemIfood {
    mensagem: string
}

export interface ImportarPedidosResultado {
    eventos: number
    mensagem: string
}

export const obterStatusIfood = async function (
    authorization: string
): Promise<StatusIfood | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<StatusIfood>>("ifoodGerenciamento/ObterStatus", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<StatusIfood>(response, "ObterStatus")
    } catch (error) {
        RegistrarLogErro(error, "ObterStatus", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const gerarAutenticacaoIfood = async function (
    authorization: string
): Promise<UserCodeIfood | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<UserCodeIfood>>("ifoodGerenciamento/GerarAutenticacao", null, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<UserCodeIfood>(response, "GerarAutenticacao")
    } catch (error) {
        RegistrarLogErro(error, "GerarAutenticacao", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const confirmarAutenticacaoIfood = async function (
    authorization: string,
    payload: { authorizationCodeVerifier: string; userCode: string }
): Promise<MensagemIfood | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<MensagemIfood>>("ifoodGerenciamento/ConfirmarAutenticacao", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<MensagemIfood>(response, "ConfirmarAutenticacao")
    } catch (error) {
        RegistrarLogErro(error, "ConfirmarAutenticacao", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const apagarCredenciaisIfood = async function (
    authorization: string
): Promise<void | ErroApi> {
    try {
        const response = await api
            .delete<{ codErro?: number; mensagem?: string }>(
                "ifoodGerenciamento/ApagarCredenciais",
                {
                    headers: authHeader(authorization),
                }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        if (response.data && response.data.codErro !== undefined && response.data.codErro > 0) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
    } catch (error) {
        RegistrarLogErro(error, "ApagarCredenciais", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const associarLojaIfood = async function (
    authorization: string,
    merchantUUID: string
): Promise<StatusIfood | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<StatusIfood>>(
                "ifoodGerenciamento/AssociarLoja",
                { merchantUUID },
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<StatusIfood>(response, "AssociarLoja")
    } catch (error) {
        RegistrarLogErro(error, "AssociarLoja", merchantUUID)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const desassociarLojaIfood = async function (
    authorization: string
): Promise<StatusIfood | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<StatusIfood>>(
                "ifoodGerenciamento/DesassociarLoja",
                {},
                {
                    headers: authHeader(authorization),
                }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<StatusIfood>(response, "DesassociarLoja")
    } catch (error) {
        RegistrarLogErro(error, "DesassociarLoja", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const importarCardapiosIfood = async function (
    authorization: string,
    payload: { validarCodigoPDV: boolean; atualizar: boolean }
): Promise<MensagemIfood | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<MensagemIfood>>("ifoodGerenciamento/ImportarCardapios", payload, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<MensagemIfood>(response, "ImportarCardapios")
    } catch (error) {
        RegistrarLogErro(error, "ImportarCardapios", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// Sincroniza o cardápio do iFood PARA o cardápio digital da Cliente Fiel
// (baixa do iFood e sobrescreve o cardápio digital). Operação DESTRUTIVA — o
// backend deve fazer o backup do cardápio digital atual antes de sobrescrever.
// Ver especificação em docs/backend/sincronizacao-cardapio-ifood.md.
export const importarCardapioIfoodParaDigital = async function (
    authorization: string
): Promise<MensagemIfood | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<MensagemIfood>>(
                "ifoodGerenciamento/ImportarCardapioParaCardapioDigital",
                {},
                {
                    headers: authHeader(authorization),
                }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<MensagemIfood>(response, "ImportarCardapioParaCardapioDigital")
    } catch (error) {
        RegistrarLogErro(error, "ImportarCardapioParaCardapioDigital", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// Publica o cardápio digital da Cliente Fiel NO iFood (usa o cardápio digital
// para popular/sobrescrever o cardápio do iFood). Operação DESTRUTIVA no iFood —
// o backend deve fazer o backup do cardápio do iFood atual antes de sobrescrever.
// Ver especificação em docs/backend/sincronizacao-cardapio-ifood.md.
export const publicarCardapioDigitalNoIfood = async function (
    authorization: string
): Promise<MensagemIfood | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<MensagemIfood>>(
                "ifoodGerenciamento/PublicarCardapioDigital",
                {},
                {
                    headers: authHeader(authorization),
                }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<MensagemIfood>(response, "PublicarCardapioDigital")
    } catch (error) {
        RegistrarLogErro(error, "PublicarCardapioDigital", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const removerCardapiosIfood = async function (
    authorization: string
): Promise<void | ErroApi> {
    try {
        const response = await api
            .delete<{ codErro?: number; mensagem?: string }>(
                "ifoodGerenciamento/RemoverCardapios",
                {
                    headers: authHeader(authorization),
                }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        if (response.data && response.data.codErro !== undefined && response.data.codErro > 0) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
    } catch (error) {
        RegistrarLogErro(error, "RemoverCardapios", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const importarPedidosIfood = async function (
    authorization: string
): Promise<ImportarPedidosResultado | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<ImportarPedidosResultado>>(
                "ifoodGerenciamento/ImportarPedidos",
                {},
                {
                    headers: authHeader(authorization),
                }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<ImportarPedidosResultado>(response, "ImportarPedidos")
    } catch (error) {
        RegistrarLogErro(error, "ImportarPedidos", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
